import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { BuilderReferenceService } from '@zayan-erp/home-building';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { map, of } from 'rxjs';
import { DsmAddendumService } from '../../../services/dsm-addendum.service';
import { DsmCreateAddendumPayload, DsmAddendumCreatedEvent } from '../../../models/dsm-deal-options.models';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

@Component({
  selector: 'dsm-add-addendum-modal',
  standalone: true,
  imports: [ReactiveFormsModule, GenericDropdownComponent, ButtonModule, FloatLabelModule],
  templateUrl: './dsm-add-addendum-modal.component.html'
})
export class DsmAddAddendumModalComponent {
  readonly #fb = inject(FormBuilder);
  readonly #builderReferenceService = inject(BuilderReferenceService);
  readonly #addendumService = inject(DsmAddendumService);

  lotId = input<string>('');
  dealId = input<string>('');

  readonly submitPayload = signal<DsmCreateAddendumPayload | null>(null);

  readonly builderReferences = toSignal(
    this.#builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  readonly form = this.#fb.group({
    builderReference: [null as string | null, Validators.required]
  });

  readonly createResource = rxResourceFixed({
    params: () => this.submitPayload(),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params) return of(null);
      return this.#addendumService.createAddendum(params);
    }
  });

  readonly isSubmitting = computed(() => this.createResource.isLoading());

  saved = output<DsmAddendumCreatedEvent>();
  dialogClose = output<void>();

  constructor() {
    effect(() => {
      const result = this.createResource.value();
      const payload = this.submitPayload();
      if (result !== undefined && result !== null && payload) {
        const selectedReference = this.builderReferences().find(ref => ref.id === payload.builderReferenceId);
        this.saved.emit({
          builderReferenceId: payload.builderReferenceId,
          label: selectedReference?.firstLabel ?? payload.builderReferenceId
        });
        this.dialogClose.emit();
      }
    });
  }

  close(): void {
    this.dialogClose.emit();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.controls.builderReference.value;
    const lotId = this.lotId();
    if (value === null || !lotId) return;

    const payload: DsmCreateAddendumPayload = {
      builderReferenceId: value,
      lotId
    };
    const dealId = this.dealId();
    if (dealId) payload.dealId = dealId;
    this.submitPayload.set(payload);
  }
}
